import React from 'react'
import styled from 'styled-typed'
import { Container } from 'uiComponents/pageElements'
import { PageTitle } from 'uiComponents/typography'
import { TableLoader } from 'uiComponents/loaders'
import { ValidationMessage } from 'uiComponents/form/formElements'
import { ActionButton } from 'uiComponents/buttons'
import { History } from 'history'
import { ConfirmationDialog } from 'uiComponents/popups/confirmationDialog'
import { ListItem } from './listItem'
import { TextInput } from 'uiComponents/input'
import { TicketTemplatesService, Template, TemplateBody, TemplateType } from 'ticketTemplates/ticketTemplatesService'
import { TopButtonWrapper } from 'uiComponents/settingsContainer'
import { CONTAINER_BOX_SHADOW } from 'uiComponents/settingsContainer'
import BaseKnowledgeLink from 'uiComponents/typography/BaseKnowledgeLink'
import { useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'
import { useTranslation } from 'translations'

const ListContainer = styled.div`
    background: ${(props) => props.theme.colors.white};
    border-radius: 6px;
    min-height: 7.5em;
    box-shadow: ${CONTAINER_BOX_SHADOW};
`
const WhiteContainer = styled.div`
    background: ${(props) => props.theme.colors.white};
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25em 1.75em;
    border-bottom: 2px solid ${(props) => props.theme.colors.background};

    &:last-child {
        border: none;
    }
`
const TemplateName = styled.div`
    font-weight: bold;
    margin-bottom: 0.9em;
`

export const ContainerHeader = styled.div`
    background-color: ${(props) => props.theme.colors.tableRow};
    padding: 0.4em 1.875rem;
    margin-bottom: 0.9em;
`

interface TicketTemplatesOverviewPageProps {
    history: History
    ticketTemplatesService: TicketTemplatesService
}

function TicketTemplatesOverviewPage(props: TicketTemplatesOverviewPageProps) {
    const { accountSlug } = useParams<BaseRouteParams>()
    const { t } = useTranslation()
    const [templateList, setTemplateList] = React.useState<Template[]>([])
    const [showPreview, setShowPreview] = React.useState<boolean>(false)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = React.useState<boolean>(false)
    const [addingModeOn, setAddingModeOn] = React.useState<boolean>(false)
    const [newTemplateName, setNewTemplateName] = React.useState<string>('')
    const [newTemplateNameError, setNewTemplateNameError] = React.useState<string>('')
    const [templateTobeDeleted, setTemplateTobeDeleted] = React.useState<string | null>(null)
    const [templateType, setTemplateType] = React.useState<TemplateType>('TICKET')
    const [path, setPath] = React.useState<'ticket_templates' | 'gift_card_templates'>('ticket_templates')

    async function getData() {
        try {
            setLoading(true)
            const templType = props.history.location.pathname.includes('gift_card_templates') ? 'VOUCHER' : 'TICKET'
            setTemplateType(templType)
            setPath(templType === 'VOUCHER' ? 'gift_card_templates' : 'ticket_templates')
            const templates = await props.ticketTemplatesService.getTemplatesList(accountSlug, templType)
            setTemplateList(templates)
        } catch {
            showErrorMessage(t('ticketTemplates.overview.listLoadError'))
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => {
        getData()
    }, [accountSlug, props.history.location.pathname])

    React.useEffect(() => {
        if (!!newTemplateName.replace(/ /g, '')) {
            setNewTemplateNameError('')
        }
        if (newTemplateName.length > 100) {
            setNewTemplateNameError(t('ticketTemplates.overview.nameTooLong'))
        }
    }, [newTemplateName])

    React.useEffect(() => {
        if (!addingModeOn) {
            setNewTemplateName('')
            setNewTemplateNameError('')
        }
    }, [addingModeOn])

    const onPreview = () => {
        setShowPreview(true)
    }

    const DEFAULT_PAYLOAD: TemplateBody = {
        name: 'Test template config',
        image: '',
        useBannerImage: false,
        printingRequired: false,
        showVat: false,
        showTapQrCode: true,
        additionalInfo: null,
        hidePrice: 'NEVER',
        showBarcode: true,
        showBarcodeNumber: true,
        showFullName: false,
        showOrderNo: true,
        showEmail: true,
        showOptions: false,
        templateType: templateType,
    }

    const createTemplate = async () => {
        if (!newTemplateName.replace(/ /g, '')) {
            setNewTemplateNameError(t('ticketTemplates.overview.nameRequired'))
        } else {
            try {
                const config = await props.ticketTemplatesService.createTemplate(accountSlug, {
                    ...DEFAULT_PAYLOAD,
                    name: newTemplateName,
                })
                props.history.push(`/account/${accountSlug}/engage/${path}/${config.id}`)
            } catch {
                showErrorMessage(t('ticketTemplates.overview.saveError'))
            }
        }
    }
    const updateTemplateName = async (id: string, v: string) => {
        try {
            const config = await props.ticketTemplatesService.updateTemplateName(accountSlug, id, v)
            const newTemplates = templateList.map((tpl) => (tpl.id === id ? config : tpl))
            setTemplateList(newTemplates)
        } catch {
            showErrorMessage(t('ticketTemplates.overview.renameError'))
        }
    }

    const deleteTemplate = async () => {
        if (!templateTobeDeleted) {
            return
        }
        try {
            await props.ticketTemplatesService.deleteTemplate(accountSlug, templateTobeDeleted)
            props.history.replace(`/account/${accountSlug}/engage/${path}/`)
            setShowDeleteConfirmationDialog(false)
            getData()
            showSuccessMessage(t('ticketTemplates.overview.deleteSuccess'))
        } catch {
            showErrorMessage(t('ticketTemplates.overview.deleteError'))
        }
    }

    return (
        <Container id="ticket-template-overview">
            {showPreview && <></>}
            <PageTitle>
                {templateType === 'VOUCHER' ? t('ticketTemplates.overview.titleGiftCard') : t('ticketTemplates.overview.titleTicket')}
            </PageTitle>
            {showDeleteConfirmationDialog && (
                <ConfirmationDialog
                    title={t('ticketTemplates.overview.deleteConfirmTitle')}
                    text=""
                    buttonText={t('common.delete')}
                    destructive
                    onCancel={() => setShowDeleteConfirmationDialog(false)}
                    onConfirm={deleteTemplate}
                />
            )}
            {!addingModeOn && (
                <TopButtonWrapper>
                    <ActionButton id="newTemplate" size="medium" onClick={(e) => setAddingModeOn(true)} kind="action">
                        {t('ticketTemplates.overview.addNew')}
                    </ActionButton>
                </TopButtonWrapper>
            )}
            {addingModeOn && (
                <WhiteContainer>
                    <div style={{ width: '24em', display: 'flex', alignItems: 'center' }}>
                        <TextInput
                            placeholder={t('ticketTemplates.overview.namePlaceholder')}
                            id="ticketTemplateName"
                            value={newTemplateName}
                            onChange={(e) => setNewTemplateName(e.target.value)}
                            status={!!newTemplateNameError ? 'error' : 'normal'}
                            block
                        />
                        <ValidationMessage className={!!newTemplateNameError ? 'validation-message-visible' : ''}>
                            {newTemplateNameError}
                        </ValidationMessage>
                    </div>
                    <div>
                        <ActionButton
                            kind="action"
                            secondary
                            onClick={() => setAddingModeOn(false)}
                            style={{ marginRight: '1.5em' }}
                        >
                            {t('common.cancel')}
                        </ActionButton>
                        <ActionButton id="saveTemplate" onClick={createTemplate} style={{ marginRight: '2em' }}>
                            {t('common.save')}
                        </ActionButton>
                    </div>
                </WhiteContainer>
            )}
            <ListContainer>
                {loading && <TableLoader style={{ paddingTop: '2em' }} />}
                {!loading && (
                    <>
                        {templateList.length === 0 && (
                            <WhiteContainer>
                                <TemplateName>{t('ticketTemplates.overview.emptyState')}</TemplateName>
                            </WhiteContainer>
                        )}
                        {templateList.map((tmp) => (
                            <ListItem
                                key={tmp.id}
                                template={tmp}
                                onPreview={onPreview}
                                onNameEdit={(v: string) => updateTemplateName(tmp.id, v)}
                                onDelete={() => {
                                    setShowDeleteConfirmationDialog(true)
                                    setTemplateTobeDeleted(tmp.id)
                                }}
                                accountSlug={accountSlug}
                                history={props.history}
                                path={path}
                            />
                        ))}
                    </>
                )}
            </ListContainer>
            {templateType === 'VOUCHER' && (
                <BaseKnowledgeLink link="https://support.convious.com/help/how-to-set-up-gift-card-templates" />
            )}
        </Container>
    )
}

export default TicketTemplatesOverviewPage
